import { createI18n } from 'vue-i18n'
import { fetchWithTimeout } from './fetchWithTimeout'

/**
 * locale 文档拉取的**超时预算（毫秒）**（T-138）。
 *
 * 依据（不是拍脑袋）：
 * - **必须远小于用户能忍受的白屏时长**，也要小于启动链的既有 e2e 就绪预算
 *   （`tests/e2e/readiness.ts` 的 `READY_MS = 5000`——「挂载」那一段 5s，T-133 建立）。
 *   预算 ≥ 5000 会让 locale 一挂起就必然撑爆该就绪判据 ⇒ 取 5000 的 1/5。
 * - **必须大于本机/局域网最坏正常耗时**：e2e 实测首访 locale 文档为**数十毫秒**量级
 *   （`/locales/config.json` → `/locales/zh.json` → `/locales/en.json` 串行，均在 100ms 内）；
 *   线上同域静态资源（见 AGENTS.md「端口与部署约定」：前端页面与静态同域）同量级。
 *   ⇒ 1000ms 已留出 **≥10×** 余量，不会把「慢但正常」误判为失败。
 * - **代价对称性**：locale 挂了也只是回落到空语言包（界面显示键名，不是崩溃），
 *   而「等下去」的代价是**永久白屏** ⇒ 宁可早失败也不可永不 settle。
 */
const LOCALE_FETCH_TIMEOUT_MS = 1000

/**
 * Language configuration interface
 */
export interface LocaleConfig {
  code: string
  name: string
  nativeName: string
  file: string
}

export interface I18nConfig {
  defaultLocale: string
  fallbackLocale: string
  availableLocales: LocaleConfig[]
}

// Default config (will be overwritten by config.json)
let config: I18nConfig = {
  defaultLocale: 'zh',
  fallbackLocale: 'zh',
  availableLocales: []
}

// Create i18n instance with empty messages (will be loaded from JSON)
const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: 'zh', // Will be updated after config loads
  fallbackLocale: 'zh',
  messages: {}, // Start empty, load from JSON files
  missingWarn: false, // Suppress missing translation warnings
  fallbackWarn: false
})

/**
 * Load i18n configuration from JSON
 *
 * T-138：改用 `fetchWithTimeout` —— 挂起（网络黑洞/网关未就绪）时**必然在预算内失败**，
 * 走下方既有 `catch` 分支返回默认 config（不再让 `await initI18n()` 永不返回 ⇒ 白屏）。
 * 失败**不静默**：`console.warn`/`console.error` 保留。
 */
export async function loadI18nConfig(): Promise<I18nConfig> {
  try {
    const response = await fetchWithTimeout('/locales/config.json', { timeoutMs: LOCALE_FETCH_TIMEOUT_MS })
    if (!response.ok) {
      console.warn('Failed to load i18n config, using defaults')
      return config
    }
    config = await response.json()
    return config
  } catch (error) {
    console.error('Error loading i18n config:', error)
    return config
  }
}

/**
 * Load locale messages from JSON file
 * Returns false if loading fails (for fallback handling)
 *
 * T-138：同 `loadI18nConfig` —— 超时 ⇒ 走既有 `return false` 分支（上层据此回落/告警）。
 */
export async function loadLocaleMessages(locale: string): Promise<boolean> {
  try {
    // Find the file name for this locale
    const localeConfig = config.availableLocales.find(l => l.code === locale)
    const fileName = localeConfig?.file || `${locale}.json`

    const response = await fetchWithTimeout(`/locales/${fileName}`, { timeoutMs: LOCALE_FETCH_TIMEOUT_MS })
    if (!response.ok) {
      console.warn(`Failed to load locale: ${locale}`)
      return false
    }
    const messages = await response.json()
    i18n.global.setLocaleMessage(locale, messages)
    return true
  } catch (error) {
    console.error(`Error loading locale ${locale}:`, error)
    return false
  }
}

/**
 * Initialize i18n by loading config and all available locales
 */
export async function initI18n(): Promise<void> {
  // Step 1: Load configuration
  await loadI18nConfig()

  // Step 2: Set fallback locale from config
  i18n.global.fallbackLocale.value = config.fallbackLocale

  // Step 3: Load fallback locale first (ensures we always have messages)
  const fallbackLoaded = await loadLocaleMessages(config.fallbackLocale)
  if (!fallbackLoaded) {
    console.error('Critical: Failed to load fallback locale')
  }

  // Step 4: Load all other available locales
  const otherLocales = config.availableLocales
    .filter(l => l.code !== config.fallbackLocale)
    .map(l => l.code)

  await Promise.all(
    otherLocales.map(async (locale) => {
      const loaded = await loadLocaleMessages(locale)
      if (!loaded) {
        // If a locale fails to load, copy fallback messages
        console.warn(`Using fallback for locale: ${locale}`)
        const fallbackMessages = i18n.global.getLocaleMessage(config.fallbackLocale)
        i18n.global.setLocaleMessage(locale, fallbackMessages)
      }
    })
  )

  // Step 5: Restore saved locale preference or use default
  const savedLocale = localStorage.getItem('locale')
  const targetLocale = savedLocale && isLocaleAvailable(savedLocale)
    ? savedLocale
    : config.defaultLocale

  setLocale(targetLocale)
}

/**
 * Check if a locale is available
 */
export function isLocaleAvailable(locale: string): boolean {
  return config.availableLocales.some(l => l.code === locale)
}

/**
 * Get available locales list
 */
export function getAvailableLocales(): LocaleConfig[] {
  return config.availableLocales
}

/**
 * Get i18n configuration
 */
export function getI18nConfig(): I18nConfig {
  return config
}

/**
 * Set current locale
 */
export function setLocale(locale: string) {
  if (!isLocaleAvailable(locale)) {
    console.warn(`Locale ${locale} not available, using fallback`)
    locale = config.fallbackLocale
  }
  i18n.global.locale.value = locale
  localStorage.setItem('locale', locale)

  // Update HTML lang attribute
  const htmlLang = locale === 'zh' ? 'zh-CN' : locale === 'en' ? 'en' : locale
  document.documentElement.lang = htmlLang
  document.documentElement.setAttribute('xml:lang', htmlLang)

  const contentLanguageMeta = document.querySelector('meta[http-equiv="Content-Language"]')
  if (contentLanguageMeta) {
    contentLanguageMeta.setAttribute('content', htmlLang)
  }
  
  // For 'raw' locale, ensure fallback is also 'raw' so missing keys show the key itself
  if (locale === 'raw') {
    i18n.global.fallbackLocale.value = 'raw'
  } else {
    i18n.global.fallbackLocale.value = config.fallbackLocale
  }
}

/**
 * Get current locale
 */
export function getLocale(): string {
  return i18n.global.locale.value
}

/**
 * Helper to merge additional messages from API (for dynamic updates)
 */
export function mergeMessages(messages: Record<string, any>) {
  Object.keys(messages).forEach((locale) => {
    i18n.global.mergeLocaleMessage(locale, messages[locale])
  })
}

// Backwards compatibility alias
export const setMessages = mergeMessages

export default i18n
